import api from './api';

export interface ProcedureResult {
  rows: Record<string, unknown>[];
  columns?: string[];
  rowCount?: number;
}

/**
 * Map legacy SQL Server stored-proc names to the new Nexus
 * bolusnotes_analytics handler actions. These were inlined as JS
 * actions during Phase B.4 of the BolusNotes Nexus port
 * (2026-05-24). Wire shape:
 *
 *   legacy:  POST /v1/db/bolus-notes/procedures/sp_<x>/execute
 *            body  { params: { userId, startDate, endDate } }
 *            resp  { rows: [...] }
 *
 *   new:     POST /v1/app/bolus_notes/fn/bolusnotes_analytics
 *            body  { action: 'get_glucose_<x>', userId, startDate, endDate }
 *            resp  { success, message, data: [...] }   (Nexus envelope)
 *
 * Keep the public `executeBolusProcedure(procName, params)` signature
 * stable so the 4 call sites in Analytics.tsx + LiveGlucoseChart.tsx
 * don't need to change. Re-wrap the bare array Nexus returns as
 * `{ rows: [...] }` to preserve the ProcedureResult contract.
 */
const PROC_TO_ACTION: Record<string, string> = {
  sp_glucose_statistics: 'get_glucose_statistics',
  sp_glucose_agp: 'get_glucose_agp',
  sp_glucose_daily_overlay: 'get_glucose_daily_overlay',
  sp_glucose_readings: 'get_glucose_readings',
  sp_glucose_readings_extent: 'get_glucose_readings_extent',
};

export async function executeBolusProcedure(
  procName: string,
  params?: Record<string, unknown>
): Promise<ProcedureResult> {
  const action = PROC_TO_ACTION[procName];
  if (!action) {
    throw new Error(
      `executeBolusProcedure: no Nexus action mapping for legacy proc '${procName}'`,
    );
  }
  // BOLUS_NOTES-72 / NEXUS-100: the AGP and daily-overlay procs group BY TIME
  // OF DAY, and those buckets are only meaningful in the viewer's zone. The
  // server buckets in UTC unless a caller names one — and this chart renders
  // the returned bucket index straight to an "HH:MM" label with no shift, so
  // without this the 8am peak is drawn at noon wherever the DB runs UTC.
  //
  // The service stays timezone-free; the caller converts. Only the two
  // time-of-day procs take it — statistics has no such axis.
  const BUCKETS_BY_TIME_OF_DAY = new Set(['get_glucose_agp', 'get_glucose_daily_overlay']);
  const timeZone = BUCKETS_BY_TIME_OF_DAY.has(action)
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : undefined;

  const res = await api.post('/v1/app/bolus_notes/fn/bolusnotes_analytics', {
    action,
    ...(params ?? {}),
    ...(timeZone ? { timeZone } : {}),
  });
  // Nexus envelopes bare handler return values as { success, message, data }.
  // The analytics handlers return arrays; unwrap data into the ProcedureResult shape.
  const rows: Record<string, unknown>[] =
    (res.data?.data as Record<string, unknown>[]) ??
    (Array.isArray(res.data) ? (res.data as Record<string, unknown>[]) : []);
  return { rows, rowCount: rows.length };
}

/** Read numeric field from SQL row (handles varying column casing). */
export function pickNum(row: Record<string, unknown> | undefined, ...keys: string[]): number {
  if (!row) return 0;
  for (const k of keys) {
    const v = row[k];
    if (v != null && v !== '') return Number(v);
  }
  return 0;
}

export interface GlucoseStatisticsParsed {
  totalReadings: number;
  gmi: number;
  average: number;
  sd: number;
  cv: number;
  median: number;
  highest: number;
  lowest: number;
  veryLowPct: number;
  lowPct: number;
  targetPct: number;
  highPct: number;
  veryHighPct: number;
  daysActive: number;
  cgmActivePercent: number;
}

export function parseStatisticsRow(row: Record<string, unknown> | undefined): GlucoseStatisticsParsed | null {
  if (!row) return null;
  const total = pickNum(row, 'totalReadings', 'totalreadings');
  if (total <= 0) return null;
  return {
    totalReadings: total,
    gmi: pickNum(row, 'gmi', 'GMI'),
    average: pickNum(row, 'average', 'Average'),
    sd: pickNum(row, 'sd', 'SD'),
    cv: pickNum(row, 'cv', 'CV'),
    median: pickNum(row, 'median', 'Median'),
    highest: pickNum(row, 'highest', 'Highest'),
    lowest: pickNum(row, 'lowest', 'Lowest'),
    veryLowPct: pickNum(row, 'veryLowPct', 'verylowpct'),
    lowPct: pickNum(row, 'lowPct', 'lowpct'),
    targetPct: pickNum(row, 'targetPct', 'targetpct'),
    highPct: pickNum(row, 'highPct', 'highpct'),
    veryHighPct: pickNum(row, 'veryHighPct', 'veryhighpct'),
    daysActive: pickNum(row, 'daysActive', 'daysactive'),
    cgmActivePercent: pickNum(row, 'cgmActivePercent', 'cgmactivepercent'),
  };
}

export interface AgpChartData {
  labels: string[];
  median: (number | null)[];
  p25: (number | null)[];
  p75: (number | null)[];
  targetMin: number;
  targetMax: number;
}

export function buildAgpChartData(rows: Record<string, unknown>[]): AgpChartData {
  const slotMap = new Map<number, Record<string, unknown>>();
  for (const r of rows) {
    const idx = Math.round(pickNum(r, 'slotIndex', 'slotindex'));
    if (!Number.isFinite(idx)) continue;
    slotMap.set(idx, r);
  }
  const labels: string[] = [];
  const median: (number | null)[] = [];
  const p25: (number | null)[] = [];
  const p75: (number | null)[] = [];
  for (let i = 0; i < 288; i++) {
    const mins = i * 5;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    labels.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    const row = slotMap.get(i);
    if (!row) {
      median.push(null);
      p25.push(null);
      p75.push(null);
    } else {
      const v50 = row.p50 ?? row.P50;
      const v25 = row.p25 ?? row.P25;
      const v75 = row.p75 ?? row.P75;
      median.push(v50 != null && v50 !== '' ? Number(v50) : null);
      p25.push(v25 != null && v25 !== '' ? Number(v25) : null);
      p75.push(v75 != null && v75 !== '' ? Number(v75) : null);
    }
  }
  return { labels, median, p25, p75, targetMin: 70, targetMax: 180 };
}

export interface DailyHourCell {
  hour: number;
  avg: number | null;
  count: number;
}

export interface DailyOverlayDayParsed {
  date: string;
  hours: DailyHourCell[];
}

export function buildDailyOverlayDays(rows: Record<string, unknown>[]): DailyOverlayDayParsed[] {
  const byDay = new Map<string, Map<number, { avg: number; count: number }>>();
  for (const r of rows) {
    const rd = r.reading_date ?? r.readingDate;
    let dateStr = '';
    if (typeof rd === 'string') dateStr = rd.slice(0, 10);
    else if (rd && typeof rd === 'object' && 'toISOString' in rd && typeof (rd as Date).toISOString === 'function') {
      dateStr = (rd as Date).toISOString().slice(0, 10);
    }
    if (!dateStr) continue;
    const hour = Math.round(pickNum(r, 'reading_hour', 'readingHour'));
    const avg = pickNum(r, 'avg_glucose', 'avgGlucose');
    const count = Math.round(pickNum(r, 'reading_count', 'readingCount'));
    if (!byDay.has(dateStr)) byDay.set(dateStr, new Map());
    byDay.get(dateStr)!.set(hour, { avg, count });
  }
  const sorted = Array.from(byDay.keys()).sort();
  return sorted.map((date) => {
    const hm = byDay.get(date)!;
    const hours: DailyHourCell[] = [];
    for (let h = 0; h < 24; h++) {
      const cell = hm.get(h);
      hours.push({
        hour: h,
        avg: cell && cell.count > 0 ? cell.avg : null,
        count: cell?.count ?? 0,
      });
    }
    return { date, hours };
  });
}

export interface GlucoseReadingPoint {
  timestamp: string;
  glucose_value: number;
  userName?: string;
  trend?: string;
}

export function normalizeReadingRows(rows: Record<string, unknown>[]): GlucoseReadingPoint[] {
  return rows
    .map((r) => {
      const ts = r.timestamp;
      const tsStr =
        typeof ts === 'string'
          ? ts
          : ts && typeof ts === 'object' && 'toISOString' in ts && typeof (ts as Date).toISOString === 'function'
            ? (ts as Date).toISOString()
            : '';
      const gv = pickNum(r, 'glucose_value', 'glucoseValue');
      if (!tsStr || !gv) return null;
      return {
        timestamp: tsStr,
        glucose_value: gv,
        userName: (r.userName ?? r.username) as string | undefined,
        trend: r.trend as string | undefined,
      };
    })
    .filter(Boolean) as GlucoseReadingPoint[];
}

/** Local calendar date YYYY-MM-DD (matches iOS Calendar-based ranges). */
export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + n);
  return x;
}

export function addMonths(d: Date, n: number): Date {
  const x = new Date(d.getTime());
  x.setMonth(x.getMonth() + n);
  return x;
}
